import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projectAPI, bugAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Project, BugStats } from '../types';

interface ProjectWithStats extends Project {
  stats?: BugStats;
}

const Dashboard: React.FC = () => {
  const [projects, setProjects] = useState<ProjectWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user, isAdmin, logout } = useAuth();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const projectsData = await projectAPI.getAll();
      
      // 获取每个项目的BUG统计信息
      const projectsWithStats = await Promise.all(
        projectsData.map(async (project) => {
          try {
            const stats = await bugAPI.getStats(project.id);
            return { ...project, stats };
          } catch (err) {
            // 如果获取统计信息失败，使用默认值
            return { 
              ...project, 
              stats: { total: 0, pending: 0, fixed: 0, shelved: 0 } 
            };
          }
        })
      );
      
      setProjects(projectsWithStats);
    } catch (err: any) {
      setError(err.response?.data?.message || '获取项目列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleDeleteProject = async (projectId: string, projectName: string) => {
    if (window.confirm(`确定要删除项目"${projectName}"吗？\n\n⚠️ 警告：此操作将删除项目及其所有BUG数据，且不可恢复！`)) {
      try {
        await projectAPI.delete(projectId);
        fetchProjects(); // 重新获取项目列表
      } catch (err: any) {
        setError(err.response?.data?.message || '删除项目失败');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 导航栏 */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">
                Guodo! 产品缺陷管理系统
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                欢迎，{user?.realName}
              </span>
              {isAdmin && (
                <Link
                  to="/users"
                  className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
                >
                  用户管理
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="text-gray-600 hover:text-gray-500 text-sm font-medium"
              >
                退出登录
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* 主要内容 */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">项目列表</h2>
            {isAdmin && (
              <Link
                to="/project/new"
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                新建项目
              </Link>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {projects.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📁</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                暂无项目
              </h3>
              <p className="text-gray-500">
                {isAdmin ? '点击"新建项目"开始创建您的第一个项目' : '请联系管理员创建项目'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {project.name}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        project.stats?.total === 0 
                          ? 'bg-green-100 text-green-800' 
                          : project.stats?.pending && project.stats.pending > 0
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {project.stats?.total === 0 
                          ? '无BUG' 
                          : project.stats?.pending && project.stats.pending > 0
                          ? '有未修复'
                          : '已修复'
                        }
                      </span>
                    </div>
                    
                    {/* BUG统计信息 */}
                    <div className="mt-4">
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div>
                          <div className="text-lg font-bold text-gray-900">
                            {project.stats?.total || 0}
                          </div>
                          <div className="text-xs text-gray-500">总计</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-yellow-600">
                            {project.stats?.pending || 0}
                          </div>
                          <div className="text-xs text-gray-500">未修改</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-green-600">
                            {project.stats?.fixed || 0}
                          </div>
                          <div className="text-xs text-gray-500">已修改</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-gray-600">
                            {project.stats?.shelved || 0}
                          </div>
                          <div className="text-xs text-gray-500">搁置</div>
                        </div>
                      </div>
                    </div>

                    {/* 进度条 */}
                    {project.stats && project.stats.total > 0 && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>修复进度</span>
                          <span>{Math.round(((project.stats.fixed || 0) / project.stats.total) * 100)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full transition-all duration-300"
                            style={{ 
                              width: `${((project.stats.fixed || 0) / project.stats.total) * 100}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                    )}

                    <div className="mt-4">
                      <Link
                        to={`/project/${project.id}`}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium text-center block"
                      >
                        查看详情
                      </Link>
                      {isAdmin && (
                        <button
                          onClick={() => handleDeleteProject(project.id, project.name)}
                          className="w-full mt-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                        >
                          删除项目
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 